import Component from '@glimmer/component';
import { Heading, Text } from 'tio-ui/components/typography';
import { formatAsDollars } from 'tio-common/utils/format';
import { htmlSafe } from '@ember/template';
import { PhTrendUp, PhCalendarCheck, PhCurrencyDollar, PhSparkle } from 'ember-phosphor-icons';
import type { PslfProjection } from 'tio-employee/services/career-pathways-api';

interface ForgivenessFortuneTellerSignature {
  Args: {
    projection: PslfProjection;
  };
  Element: HTMLDivElement;
}

export default class ForgivenessFortuneTeller extends Component<ForgivenessFortuneTellerSignature> {
  get progressPercent() {
    return Math.round((this.args.projection.qualifyingPaymentsToDate / 120) * 100);
  }

  get progressStyle() {
    return htmlSafe(`width: ${this.progressPercent}%`);
  }

  get earnedAmount() {
    return this.args.projection.earnedToDate;
  }

  get remainingAmount() {
    return this.args.projection.projectedForgivenessAmount - this.earnedAmount;
  }

  get monthlyRate() {
    return this.args.projection.monthlyForgivenessRate;
  }

  get yearlyValue() {
    return this.monthlyRate * 12;
  }

  get forgivenessDate() {
    return new Date(this.args.projection.projectedForgivenessDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  get yearsRemaining() {
    return Math.round((this.args.projection.paymentsRemaining / 12) * 10) / 10;
  }

  // Pre-format dollar amounts
  get formattedProjectedAmount() {
    return formatAsDollars(this.args.projection.projectedForgivenessAmount, { cents: false });
  }

  get formattedEarnedAmount() {
    return formatAsDollars(this.earnedAmount, { cents: false });
  }

  get formattedRemainingAmount() {
    return formatAsDollars(this.remainingAmount, { cents: false });
  }

  get formattedMonthlyRate() {
    return formatAsDollars(this.monthlyRate, { cents: false });
  }

  get formattedYearlyValue() {
    return formatAsDollars(this.yearlyValue, { cents: false });
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div
      class="bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-600 rounded-2xl p-6 md:p-8 text-white shadow-xl"
      ...attributes
    >
      {{! Header }}
      <div class="flex items-center gap-3 mb-6">
        <div class="p-2 bg-white/20 rounded-xl">
          <PhSparkle @size={{28}} class="text-yellow-300" @weight="fill" />
        </div>
        <div>
          <Heading @level={{2}} class="text-white text-xl md:text-2xl font-bold">
            Your Forgiveness Fortune
          </Heading>
          <Text class="text-emerald-100 text-sm">Tax-free wealth you're building</Text>
        </div>
      </div>

      {{! Main Fortune Display }}
      <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-6 border border-white/20">
        <div class="text-center">
          <Text class="text-emerald-100 text-sm uppercase tracking-wider font-medium">
            Projected Tax-Free Forgiveness
          </Text>
          <div class="mt-2 mb-1">
            <span class="text-4xl md:text-5xl font-bold tracking-tight">
              {{this.formattedProjectedAmount}}
            </span>
          </div>
          <Text class="text-emerald-200 text-sm">
            in
            {{this.yearsRemaining}}
            years
          </Text>
        </div>
      </div>

      {{! Progress Bar }}
      <div class="mb-6">
        <div class="flex justify-between text-sm mb-2">
          <span class="text-emerald-100">
            <span class="font-semibold text-white">{{this.formattedEarnedAmount}}</span>
            earned
          </span>
          <span class="text-emerald-100">
            <span class="font-semibold text-white">{{this.formattedRemainingAmount}}</span>
            to go
          </span>
        </div>
        <div class="h-4 bg-white/20 rounded-full overflow-hidden">
          <div
            class="h-full bg-gradient-to-r from-yellow-400 to-yellow-300 rounded-full transition-all duration-1000 ease-out relative"
            style={{this.progressStyle}}
          >
            <div
              class="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse"
            ></div>
          </div>
        </div>
        <div class="flex justify-between mt-2">
          <Text class="text-xs text-emerald-200">
            {{@projection.qualifyingPaymentsToDate}}
            payments made
          </Text>
          <Text class="text-sm font-semibold text-white">
            {{this.progressPercent}}% complete
          </Text>
        </div>
      </div>

      {{! Stats Grid }}
      <div class="grid grid-cols-2 gap-3">
        <div class="bg-white/10 rounded-xl p-4 border border-white/10">
          <div class="flex items-center gap-2 mb-1">
            <PhCalendarCheck @size={{18}} class="text-emerald-200" />
            <Text class="text-emerald-200 text-xs font-medium">Forgiveness Date</Text>
          </div>
          <Text class="font-semibold text-white">{{this.forgivenessDate}}</Text>
        </div>

        <div class="bg-white/10 rounded-xl p-4 border border-white/10">
          <div class="flex items-center gap-2 mb-1">
            <PhCurrencyDollar @size={{18}} class="text-emerald-200" />
            <Text class="text-emerald-200 text-xs font-medium">Each Payment Earns</Text>
          </div>
          <Text class="font-semibold text-white">{{this.formattedMonthlyRate}}</Text>
        </div>
      </div>

      {{! Value Message }}
      <div
        class="mt-6 p-4 bg-gradient-to-r from-yellow-500/20 to-amber-500/20 rounded-xl border border-yellow-400/30"
      >
        <div class="flex items-start gap-3">
          <PhTrendUp @size={{24}} class="text-yellow-300 flex-shrink-0 mt-0.5" />
          <div>
            <Text class="text-white font-medium">
              💡 Staying on track this year is worth
              <span class="font-bold text-yellow-300">
                {{this.formattedYearlyValue}}
              </span>
              to you.
            </Text>
            <Text class="text-emerald-100 text-sm mt-1">
              Every month you stay in public service adds
              {{this.formattedMonthlyRate}}
              to your forgiveness fortune.
            </Text>
          </div>
        </div>
      </div>
    </div>
  </template>
}
